import { AfterViewInit, ApplicationRef, Component, Inject, inject, NgZone, OnDestroy, OnInit, Renderer2, DOCUMENT, viewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { combineLatest, merge, take, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import {
  GoogleAnalyticsService,
  LoggedInUserSettings,
  UtilityService,
  PGHOktaService,
  PreInitSettingsService,
  PGHAppLoadService,
  StorageService,
  AsappChatInitializer,
  OneTagService,
  ISelectedPlan,
  AppConstants,
} from './core/core';
import { datadogRum } from '@datadog/browser-rum';
import { ADPResourceService } from 'digital-core';
import { NgClass } from '@angular/common';
import { OktaAuth } from '@okta/okta-auth-js';
import { AsappCustomerInfo } from './model/asapp-chat/asapp-chat.dto';
import { ICustomerInfo } from './model/portal-profile/IPortalProfile';
import { GTMEvents } from './model/google-tag-manager/api-status';
import { MatSidenavContent, MatSidenavContainer, MatSidenav } from 'src/app/core/core';
import { HeaderComponent } from './core/components/header/header.component';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import { AssurantLoaderComponent, ScrollTrackerDirective } from './shared';
import { LoaderProgressComponent } from './shared/components/loader-progress/loader-progress.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { HeaderService } from './core/services/header.service';
import { UserClaims } from './model/okta/userClaims';
import { MiawChatInitializer } from 'src/app/core/services/miaw-chat.initializer';
import { MiawCustomerInfo } from './model/salesforce-chat/miaw-chat.dto';
import { SiteRedirectService } from './core/services/site-redirect.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    HeaderComponent,
    MatSidenavContainer,
    MatSidenav,
    SidenavComponent,
    MatSidenavContent,
    AssurantLoaderComponent,
    LoaderProgressComponent,
    RouterOutlet,
    FooterComponent,
    NgClass,
    ScrollTrackerDirective,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  isAuthenticated: boolean;
  isSidenavShowing: boolean = false;
  isNative: boolean;
  isFixedHeader: boolean = false;
  programName: string;

  // Routes that render their own full-bleed chrome (no header/sidenav/footer).
  private readonly CHROMELESS_ROUTES = ['/health-check', '/renew'];

  private unListen: () => void;

  private readonly content = viewChild(MatSidenavContent);

  private readonly siteRedirectService = inject(SiteRedirectService);

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    public oktaAuthService: OktaAuthStateService,
    public router: Router,
    public route: ActivatedRoute,
    public title: Title,
    private loggedInUserSettings: LoggedInUserSettings,
    private utilityService: UtilityService,
    private preInitSettingsService: PreInitSettingsService,
    private pghOktaService: PGHOktaService,
    public resourceService: ADPResourceService,
    private appLoadService: PGHAppLoadService,
    private storageService: StorageService,
    @Inject(DOCUMENT) private document,
    private renderer: Renderer2,
    private googleAnalyticsService: GoogleAnalyticsService,
    private asappChatInitializer: AsappChatInitializer,
    private oneTagService: OneTagService,
    private appRef: ApplicationRef,
    private zone: NgZone,
    public headerService: HeaderService,
    private miawChatInitializer: MiawChatInitializer
  ) {
    if (window.location.pathname.indexOf('accept-invite') > -1) {
      this.storageService.clearLocal();
    }
    this.route.queryParams.subscribe((params) => {
      this.isNative = (params.isNative && params.isNative.toLowerCase().trim() === 'true') || this.utilityService.isNativeApp;
    });
  }

  async ngOnInit() {
    // Old-domain -> new-domain move, driven by CMS (Global_OldToNewSite_Redirect_Mapping).
    this.siteRedirectService.init();

    if (this.preInitSettingsService.getSettings().isDatadogRUMEnabled?.toLowerCase() == 'true') {
      this.initDatadogRUM();
    }

    // Remove all Okta Storage when the User leaves the app only on mobile
    if (this.isNative) {
      window.onbeforeunload = () => {
        for (let key in localStorage) {
          if (key.startsWith('okta')) {
            localStorage.removeItem(key);
          }
        }
      };
    }

    this.setTheme();
    this.oktaAuthService.authState$.subscribe((authState) => (this.isAuthenticated = authState.isAuthenticated));
    this.isAuthenticated = this.pghOktaService.isTokenValid();

    combineLatest([
      this.loggedInUserSettings.$selectedPlan,
      this.loggedInUserSettings.$userClaims,
      this.loggedInUserSettings.$customerInfo,
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    ])
      .pipe(
        filter(([selectedPlan, user, customerInfo, _]: [ISelectedPlan, UserClaims, ICustomerInfo, any]) => {
          return (!!selectedPlan && !!user && !!customerInfo) || (!selectedPlan && !user && !customerInfo);
        })
      )
      .subscribe(([selectedPlan, user, customerInfo, _]: [ISelectedPlan, UserClaims, ICustomerInfo, any]) => {
        // add one script with the correct key
        this.oneTagService.initializeOneTag();

        this.programName = this.resourceService.getPortalSetting('Global_ProgramName').Value;
        const titleArray = this.getTitle(this.router.routerState, this.router.routerState.root);
        if (titleArray && titleArray[0] === 'My Devices') {
          const titleValue = this.resourceService.getPortalSetting('Global_MyDevices_Title').Value;
          if (titleValue) {
            titleArray[0] = titleValue;
          }
        }
        if (this.programName) {
          titleArray.push(this.programName);
        }
        const title = titleArray.join(' | ');
        this.title.setTitle(title);

        if (selectedPlan?.planNumber) {
          // Logged in
          this.googleAnalyticsService.setUserData(selectedPlan, user.sub);
          this.googleAnalyticsService.emitPageViewEvent();
          const primaryCustomer = selectedPlan.customers.find((customer) => customer.customerType === AppConstants.PLAN_CUSTOMER_TYPE.Primary);
          const phoneNumber = primaryCustomer.cellPhone?.value ?? primaryCustomer.workPhone?.value ?? primaryCustomer.homePhone?.value ?? '';
          const email = primaryCustomer.emailAddress?.value ?? '';
          const address = primaryCustomer.addresses?.find((address) => address.addressType === AppConstants.PLAN_ADDRESS_TYPE.Primary);
          const propertyAddress = address ? `${address.address1}, ${address.city}, ${address.state} ${address.postalCode}` : '';
          // this is used for asapp chat — encrypted fields and customerId come pre-computed from BE
          if (customerInfo) {
            const asappCustomerInfo: AsappCustomerInfo = {
              isAuthenticated: true,
              customerId: customerInfo.customerId,
              encryptedFirstName: customerInfo.encryptedFirstName,
              encryptedLastName: customerInfo.encryptedLastName,
              encryptedPhoneNumber: customerInfo.encryptedPhoneNumber,
              encryptedEmail: customerInfo.encryptedEmail,
              contractNumber: selectedPlan.planNumber,
              propertyAddress: propertyAddress,
              policyStatus: selectedPlan.status,
              coverageStartDate: selectedPlan.planSalesDate,
              lang: user?.locale,
            };
            this.asappChatInitializer.initialize(asappCustomerInfo);
          }

          const lineOfBusiness = this.resourceService.getPortalSetting('Global_MiawChat_Line_Of_Bussiness')?.Value;
          const inputSubject = this.resourceService.getPortalSetting('Global_MiawChat_Input_Subject')?.Value;

          const miawCustomerInfo: MiawCustomerInfo = {
            input_FirstName: primaryCustomer.firstName,
            input_LastName: primaryCustomer.lastName,
            input_Email: email,
            input_Subject: inputSubject,
            Line_of_Business: lineOfBusiness,
            Customer_Language: user?.locale,
            Messaging_Source: 'Web',
            Phone: phoneNumber,
            Account_Number: selectedPlan?.planNumber,
          };

          this.miawChatInitializer.initialize(miawCustomerInfo, user?.locale);
        } else {
          // Not logged in
          if (!this.isAuthenticated) {
            this.googleAnalyticsService.emitPageViewEvent();
          }
        }
      });
  }

  ngAfterViewInit() {
    // Add a click listener to the document
    this.unListen = this.renderer.listen('document', 'click', (event: Event) => {
      const target = event.target as HTMLElement;

      if (target && (target.classList.contains('helpButtonEnabled') || target.classList.contains('uiButton') || target.classList.contains('helpButtonLabel'))) {
        // console.log(target);
        this.googleAnalyticsService.emitClickEvent(GTMEvents.ChatOpen, 'Live Support', undefined, undefined, undefined);
      }
    });

    // Scroll to top of page on route change
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      // Optional: skip forced scroll when navigating to an anchor (fragment)
      const tree = this.router.parseUrl(this.router.url);
      if (tree.fragment) {
        return; // let Router anchorScrolling handle it
      }

      const whenStable$ = merge(
        this.appRef.isStable.pipe(filter(Boolean)),
        timer(0) // fallback: next macrotask in case isStable doesn't emit
      ).pipe(take(1));

      whenStable$.subscribe(() => {
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.scrollTop();
            // backup scroll in case late microtasks add content
            setTimeout(() => this.scrollTop(), 0);
          });
        });
      });
    });
  }

  ngOnDestroy() {
    if (this.unListen) {
      this.unListen();
    }
  }

  // recursively get title
  getTitle(state, parent) {
    const data = [];

    if (parent && parent.snapshot?.data && parent.snapshot.data?.title) {
      data.push(parent.snapshot.data.title);
    }

    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }

    return data;
  }

  setSidenavShow(isShowing: boolean) {
    this.isSidenavShowing = isShowing;
  }

  isFullScreen(): boolean {
    return this.CHROMELESS_ROUTES.some((route) => this.router.url === route || this.router.url.startsWith(`${route}?`));
  }

  initDatadogRUM() {
    const settings = this.preInitSettingsService.getSettings();
    datadogRum.init({
      applicationId: settings.datadogRUMConfig.applicationId,
      clientToken: settings.datadogRUMConfig.clientToken,
      site: 'us3.datadoghq.com',
      service: 'homewarranty-myaccount',
      env: settings.environmentName,
      version: settings.datadogRUMConfig.version,
      sessionSampleRate: parseInt(settings.datadogRUMConfig.sampleRate),
      sessionReplaySampleRate: parseInt(settings.datadogRUMConfig.replaySampleRate),
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
    });
    // app_name should always be PGH as HW is configured as a service under PGH app in Datadog RUM
    datadogRum.setGlobalContextProperty('app_name', settings.datadogRUMConfig.appName ?? 'PGH');
    datadogRum.setGlobalContextProperty('app_env', settings.environmentName);
    datadogRum.setGlobalContextProperty('cost_center', parseInt(settings.datadogRUMConfig.cid));
    datadogRum.setGlobalContextProperty('subcost_center', parseInt(settings.datadogRUMConfig.scid));
    datadogRum.setGlobalContextProperty('client_name', this.preInitSettingsService.getSiteSettings().name);
  }

  setTheme() {
    this.appLoadService.isSiteResourcesLoaded.subscribe((value) => {
      if (value) {
        if (this.resourceService.getPortalSetting('Global_Theme').Value !== '') {
          this.document.body.classList.add(this.resourceService.getPortalSetting('Global_Theme').Value);
        }
      }
    });
  }

  private scrollTop() {
    const content = this.content();
    if (content) {
      content.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }

  onFixedHeader(isFixedHeader: boolean) {
    this.isFixedHeader = isFixedHeader;
  }
}
